import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import songFileServiceInstance from '../../../services/songFileService';

export default function SongFileUploadModal({ isOpen, onClose, songId, onUploadComplete }) {
  const [selectedMidiFile, setSelectedMidiFile] = useState(null);
  const [selectedMp3File, setSelectedMp3File] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  const handleFileSelect = (e, fileType) => {
    const file = e?.target?.files?.[0];
    if (file) {
      if (fileType === 'midi') {
        setSelectedMidiFile(file);
      } else {
        setSelectedMp3File(file);
      }
    }
  };

  const handleUpload = async () => {
    if (!selectedMidiFile && !selectedMp3File) {
      setError('Please select at least one file to upload');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      // Upload MIDI file (song-level only)
      if (selectedMidiFile) {
        const { data: midiData, error: midiError } = await songFileServiceInstance?.uploadSongFile(
          selectedMidiFile,
          songId,
          'midi'
        );

        if (midiError) throw midiError;

        // Update song record with MIDI file path
        await songFileServiceInstance?.updateSongFilePath(songId, midiData?.path, 'midi');
      }

      // Upload MP3 file (song-level only)
      if (selectedMp3File) {
        const { data: mp3Data, error: mp3Error } = await songFileServiceInstance?.uploadSongFile(
          selectedMp3File,
          songId,
          'mp3'
        );

        if (mp3Error) throw mp3Error;

        // Update song record with MP3 file path
        await songFileServiceInstance?.updateSongFilePath(songId, mp3Data?.path, 'mp3');
      }

      // Success
      alert('Files uploaded successfully!');
      setSelectedMidiFile(null);
      setSelectedMp3File(null);
      onUploadComplete?.();
      onClose();
    } catch (err) {
      console.error('Upload error:', err);
      setError(err?.message || 'Failed to upload files');
    } finally {
      setUploading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm">
      <div className="bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 rounded-xl shadow-2xl max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-white flex items-center">
            <Icon name="Upload" size={28} className="mr-2" />
            Upload Song Files
          </h2>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-300 transition-colors"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        {/* Info Message */}
        <div className="bg-blue-500 bg-opacity-20 border border-blue-400 border-opacity-30 rounded-lg p-4 mb-6">
          <div className="flex items-start space-x-3">
            <Icon name="Info" size={20} className="text-blue-300 mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="text-sm font-medium text-blue-100 mb-1">
                Song-Level Files
              </h4>
              <p className="text-xs text-blue-200 leading-relaxed">
                Upload MIDI and MP3 files at the song level. These files will be shared across all character analyses for this song, ensuring consistency and reducing duplication.
              </p>
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-500 bg-opacity-20 border border-red-400 border-opacity-30 rounded-lg p-4 mb-4">
            <p className="text-red-200 text-sm">{error}</p>
          </div>
        )}

        <div className="space-y-4">
          {/* MIDI File Upload */}
          <div>
            <label className="block text-sm font-medium text-blue-100 mb-2">
              MIDI File (.mid, .midi)
            </label>
            <div className="relative">
              <input
                type="file"
                accept=".mid,.midi"
                onChange={(e) => handleFileSelect(e, 'midi')}
                disabled={uploading}
                className="w-full px-4 py-3 bg-white bg-opacity-10 border border-white border-opacity-20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              />
              {selectedMidiFile && (
                <div className="mt-2 flex items-center text-sm text-blue-200">
                  <Icon name="CheckCircle" size={16} className="mr-2 text-green-400" />
                  {selectedMidiFile?.name}
                </div>
              )}
            </div>
          </div>

          {/* MP3 File Upload */}
          <div>
            <label className="block text-sm font-medium text-blue-100 mb-2">
              MP3 File (.mp3)
            </label>
            <div className="relative">
              <input
                type="file"
                accept=".mp3"
                onChange={(e) => handleFileSelect(e, 'mp3')}
                disabled={uploading}
                className="w-full px-4 py-3 bg-white bg-opacity-10 border border-white border-opacity-20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              />
              {selectedMp3File && (
                <div className="mt-2 flex items-center text-sm text-blue-200">
                  <Icon name="CheckCircle" size={16} className="mr-2 text-green-400" />
                  {selectedMp3File?.name}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center space-x-3 mt-6">
          <button
            onClick={handleUpload}
            disabled={uploading || (!selectedMidiFile && !selectedMp3File)}
            className="flex-1 px-4 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center font-semibold"
          >
            {uploading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Uploading...
              </>
            ) : (
              <>
                <Icon name="Upload" size={20} className="mr-2" />
                Upload Files
              </>
            )}
          </button>
          <button
            onClick={onClose}
            disabled={uploading}
            className="px-4 py-3 bg-white bg-opacity-10 text-white rounded-lg hover:bg-opacity-20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}